import { groq } from "next-sanity"
import { createTranslator } from "short-uuid"

import { CommerceService } from "@/services/commerce"

import { getArticleConfiguration } from "../../shared/utils/getArticleInformation"
import { getSkuCalculatedPrice } from "../../shared/utils/getPrices"
import { getItemsWithSelections } from "./getItemsWithSelections"

/**
 * Forms action object for removing custom line item
 *
 * @param {string} id
 * @returns {{action: string, id: string}|null}
 */
export const removeCustomLineItemAction = (id) => {
  if (!id) return null

  return {
    action: "removeCustomLineItem",
    customLineItemId: id,
  }
}

/**
 * Returns the object for adding a line item (or updating existing one if one already exists)
 *
 * @param {object} targetCustomLineItem
 * @param {string} currencyCode
 * @param {object} price
 * @param {string} cartId
 * @param {number} quantity
 *
 * @returns {object}
 */
export const addCustomLineItemAction = (targetCustomLineItem, currencyCode, price, cartId, quantity) => {
  const targetCustomLineItemCustomFields = targetCustomLineItem?.custom?.fields

  if (
    !targetCustomLineItemCustomFields?.sku ||
    !targetCustomLineItemCustomFields?.productId ||
    !price?.key ||
    !cartId ||
    !currencyCode ||
    !quantity ||
    !targetCustomLineItem?.name ||
    !targetCustomLineItem?.custom?.type?.id ||
    !price?.value
  ) {
    return null
  }

  const slug =
    targetCustomLineItem?.slug ||
    `cli-${cartId}-${targetCustomLineItemCustomFields.productId}-${targetCustomLineItemCustomFields.sku}-${price.key}`

  return {
    name: targetCustomLineItem.name,
    quantity,
    money: price.value,
    action: "addCustomLineItem",
    slug,
    taxCategory: targetCustomLineItem.taxCategory,
    // custom fields for custom line item
    custom: {
      type: { id: targetCustomLineItem.custom.type.id, typeId: "type" },
      fields: {
        productId: targetCustomLineItemCustomFields.productId,
        sku: targetCustomLineItemCustomFields.sku,
        priceId: price.key,
        colorId: targetCustomLineItemCustomFields.colorId,
        additionalInformation: targetCustomLineItemCustomFields.additionalInformation,
        sendTechnicalManual: targetCustomLineItemCustomFields.sendTechnicalManual,
        sendCertificateWithOffer: targetCustomLineItemCustomFields.sendCertificateWithOffer,
        sendProductText: targetCustomLineItemCustomFields.sendProductText,
        discountPercent: targetCustomLineItemCustomFields.discountPercent,
        customPrice: targetCustomLineItemCustomFields.customPrice,
        allowMultiple: targetCustomLineItemCustomFields.allowMultiple,
        additionalComment: targetCustomLineItemCustomFields.additionalComment,
        position: targetCustomLineItemCustomFields.position,
        skuPrice: price?.skuPrice,
      },
    },
  }
}

/**
 * Finds custom line item based on key and sku
 *
 * @param {array} customLineItems
 * @param {object} item
 * @returns {object|null}
 */
export const findTargetCartCustomLineItem = (customLineItems, item, isScanToCart = false) => {
  return customLineItems.find((customLineItem) => {
    const targetCustomFields = customLineItem.custom.fields

    if (!targetCustomFields) return false

    const skuAndProdKeyMatch = targetCustomFields.sku === item.sku && targetCustomFields?.productId === item.productKey

    if (isScanToCart) {
      return skuAndProdKeyMatch
    }

    if (
      skuAndProdKeyMatch &&
      // if set as duplicate, this is not what we are looking for
      !targetCustomFields?.allowMultiple &&
      !item.allowMultiple
    ) {
      return true
    }

    return false
  })
}

/**
 * Based on inputs and configuration, provides default insert action
 *
 * @param {{cartId: string, item: object, price: object, quantity: number, customTypeId: string}}
 *
 * @returns {object}
 */
export const formDefaultInsertAction = ({ cartId, item, price, quantity, customTypeId }) => {
  if (!cartId || !item || !price || !quantity || !customTypeId) {
    throw new Error("Missing required parameters for default insert action")
  }

  const idTranslator = createTranslator("0123456789ABCDEFHIJKLMNOPQRSTUVWXYZ")
  const shortUuid = idTranslator.generate()

  const defaultSlug = `cli-${cartId}-${item.productKey}-${item.sku}-${price.key}`

  return {
    action: "addCustomLineItem",
    name: item.name,
    quantity: Number(quantity),
    money: price.value,
    slug: `${defaultSlug}${item?.allowMultiple ? `-` + shortUuid : ""}`,
    taxCategory: item.taxCategory,
    custom: {
      type: { id: customTypeId, typeId: "type" },
      fields: {
        skuPrice: price?.skuPrice,
        productId: item.productKey,
        sku: item.sku,
        priceId: price.key,
        additionalInformation: item?.additionalInformation,
        allowMultiple: item?.allowMultiple,
        discountPercent: item?.discountPercent,
        customPrice:
          typeof item?.newPrice === "number"
            ? {
                centAmount: Math.round((Number(item.newPrice) || 0) * 100),
                type: "centPrecision",
                currencyCode: price.value.currencyCode,
                fractionDigits: 2,
              }
            : undefined,
        sendProductText: item?.sendProductText,
        sendTechnicalManual: item?.sendTechnicalManual,
        additionalComment: item?.additionalComment,
        position: item?.position,
      },
    },
  }
}

/**
 * Updates custom line item object with new fields from multiple queries in insert action
 *
 * @param {{targetCustomLineItem: object, valuesToUpdate: object, customTypeId: string, price: object, lineItemUpdates: object|null}}
 * @returns {object}
 */
export const getUpdatedLineItem = ({ targetCustomLineItem, valuesToUpdate, customTypeId, price, lineItemUpdates }) => {
  if (!targetCustomLineItem || !valuesToUpdate || !customTypeId || !price) {
    throw new Error("Missing required parameters for updating line item")
  }

  const customFields = targetCustomLineItem.custom.fields

  // if this value was not set previously, there is no need to update
  // commercetools throws error when previously it was undefined and it updates to false
  const allowMultiple =
    typeof customFields?.allowMultiple !== "boolean"
      ? customFields?.allowMultiple
      : customFields?.allowMultiple || valuesToUpdate.allowMultiple

  // update the custom type with new fields
  return {
    ...targetCustomLineItem,
    custom: {
      type: targetCustomLineItem?.custom?.type,
      fields: {
        skuPrice: price.skuPrice,
        colorId: customFields?.colorId,
        priceId: price.key,
        productId: customFields?.productId || valuesToUpdate.productKey,
        sku: customFields?.sku || valuesToUpdate.sku,
        allowMultiple,
        additionalInformation: lineItemUpdates?.additionalInformationUpdated
          ? valuesToUpdate?.additionalInformation
          : customFields?.additionalInformation,
        sendTechnicalManual: lineItemUpdates?.sendTechnicalManualUpdated
          ? valuesToUpdate?.sendTechnicalManual
          : customFields?.sendTechnicalManual,
        sendCertificateWithOffer: lineItemUpdates?.sendCertificateWithOfferUpdated
          ? valuesToUpdate?.sendCertificateWithOffer
          : customFields?.sendCertificateWithOffer,
        sendProductText: lineItemUpdates?.sendProductTextUpdated
          ? valuesToUpdate?.sendProductText
          : customFields?.sendProductText,
        discountPercent: lineItemUpdates?.discountUpdated
          ? valuesToUpdate?.discountPercent
          : customFields?.discountPercent,
        customPrice: lineItemUpdates?.priceUpdated
          ? {
              centAmount: Math.round((Number(valuesToUpdate?.newPrice) || 0) * 100),
              type: "centPrecision",
              currencyCode: price.value.currencyCode,
              fractionDigits: 2,
            }
          : customFields?.customPrice,
        additionalComment: lineItemUpdates?.additionalCommentUpdated
          ? valuesToUpdate?.additionalComment
          : customFields?.additionalComment,
        position: lineItemUpdates?.positionUpdated ? valuesToUpdate?.position : customFields?.position,
      },
    },
  }
}

/**
 * Finds updates in line item
 *
 * @param {object} customFields
 * @param {object} item
 *
 * @returns {{additionalInformationUpdated: boolean, sendProductTextUpdated: boolean, sendTechnicalManualUpdated: boolean, discountUpdated: boolean, priceUpdated: boolean}|null}
 */
export const findCustomLineItemUpdates = (customFields, item) => {
  // fields do not exist
  if (!customFields || !item) return null

  // all of the fields that can change in this request
  let additionalInformationUpdated = false
  let sendProductTextUpdated = false
  let sendTechnicalManualUpdated = false
  let sendCertificateWithOfferUpdated = false
  let discountUpdated = false
  let priceUpdated = false
  let additionalCommentUpdated = false
  let positionUpdated = false
  // setting false as default for line items, for old line items, could cause errors if not updated
  let allowMultipleUpdated = false

  // go over each field and check if it has changed
  if (item?.additionalInformation && item?.additionalInformation !== customFields?.additionalInformation) {
    additionalInformationUpdated = true
  }

  if (
    typeof item?.sendTechnicalManual === "boolean" &&
    item?.sendTechnicalManual !== customFields?.sendTechnicalManual
  ) {
    sendTechnicalManualUpdated = true
  }
  if (
    typeof item?.sendCertificateWithOffer === "boolean" &&
    item?.sendCertificateWithOffer !== customFields?.sendCertificateWithOffer
  ) {
    sendCertificateWithOfferUpdated = true
  }

  if (typeof item?.sendProductText === "boolean" && item?.sendProductText !== customFields?.sendProductText) {
    sendProductTextUpdated = true
  }

  if (typeof item?.discountPercent === "number" && item?.discountPercent !== customFields?.discountPercent) {
    discountUpdated = true
  }

  if (
    typeof item?.newPrice === "number" &&
    Number(item?.newPrice) * 100 !== Number(customFields?.customPrice?.centAmount)
  ) {
    priceUpdated = true
  }

  if (typeof item?.allowMultiple === "boolean" && item?.allowMultiple !== customFields?.customPrice?.allowMultiple) {
    allowMultipleUpdated = true
  }

  if (item?.additionalComment && item?.additionalComment !== customFields?.additionalComment) {
    additionalCommentUpdated = true
  }

  if (item?.position && item?.position !== customFields?.position) {
    positionUpdated = true
  }

  // if none of the items have changed, nothing to do
  if (
    !additionalInformationUpdated &&
    !sendProductTextUpdated &&
    !sendTechnicalManualUpdated &&
    !sendCertificateWithOfferUpdated &&
    !discountUpdated &&
    !priceUpdated &&
    !allowMultipleUpdated &&
    !additionalCommentUpdated &&
    !positionUpdated
  ) {
    return null
  }

  return {
    additionalInformationUpdated,
    sendProductTextUpdated,
    sendTechnicalManualUpdated,
    sendCertificateWithOfferUpdated,
    discountUpdated,
    priceUpdated,
    allowMultipleUpdated,
    additionalCommentUpdated,
    positionUpdated,
  }
}

/**
 * Retrieves list of two-k-products from the array of items
 * only requirement is to add them to the cart as a custom line item.
 *
 * @param {array} items
 * @param {sanityClient} client
 *
 * @returns {Promise<array>}
 */
export const getTwoKProducts = async (items, client) => {
  if (items.length === 0) return []

  let twoKProducts = []

  try {
    const draftsFilter = `&& !(_id in path("drafts.**"))`

    for (let item of items) {
      if (!item?.sku) continue

      const article = await client.fetch(
        groq`*[_type=="article" && sku==$sku ${draftsFilter}][0] {
          twoKArticle->{
            sku,
            "productRef": *[_type=="product" && references(^._id)][0] {
              key
            }
          }
      }`,
        { sku: item.sku },
      )

      if (
        !article?.twoKArticle?.sku ||
        !article?.twoKArticle?.productRef?.key ||
        article?.twoKArticle?.sku === item.sku
      )
        continue

      const articleItem = await getItemsWithSelections(
        [
          {
            sku: article?.twoKArticle?.sku,
            productKey: article?.twoKArticle?.productRef?.key,
            quantity: item.quantity,
            colorId: "",
            allowMultiple: true,
          },
        ],
        items[items.length - 1].position,
      )

      // index 0 because we pass only one item into the array
      twoKProducts.push(articleItem[0])
    }
  } catch (error) {
    console.warn(error)
    return []
  }

  twoKProducts = twoKProducts.filter((n) => n)

  return twoKProducts
}

/**
 * Returns insert actions for the cart
 *
 * @returns {Promise<array>}
 */
export const getInsertActions = async ({
  itemsWithSelections,
  customerCart,
  storeKeysWithChannels,
  customerGroup,
  currencyCode,
  customTypeLineItem,
  isScanToCart = false,
}) => {
  let actions = []

  if (itemsWithSelections.length < 1 || !customerCart) return actions

  for (let item of itemsWithSelections) {
    const articleConfig = await getArticleConfiguration({
      sku: item.sku,
      productKey: item.productKey,
    })
    if (!articleConfig) continue

    let itemQuantity = Number(item.quantity)

    if (articleConfig?.sellableInDecimals) {
      itemQuantity = Math.round(itemQuantity * 1000)
    }

    const price = await getSkuCalculatedPrice({
      sku: item.sku,
      channels: storeKeysWithChannels,
      group: customerGroup,
      country: customerCart.billingAddress.country,
      currencyCode,
      articleConfig,
      quantity: itemQuantity,
    })

    const { customLineItems } = customerCart

    // price does not exist in commercetools and no line items are present in the cart
    // will not be able to form custom line item as not enough information is present
    if (customLineItems.length <= 0 && !price) {
      continue
    }

    const defaultInsertAction = formDefaultInsertAction({
      cartId: customerCart.id,
      item,
      price,
      quantity: itemQuantity,
      customTypeId: customTypeLineItem.id,
    })

    // forming a new line item, no line items are present in the cart
    if (customLineItems.length <= 0) {
      actions.push(defaultInsertAction)
      continue
    }

    const targetCustomLineItem = findTargetCartCustomLineItem(customLineItems, item, isScanToCart)

    // Item was not in the cart, return action that adds it to the cart
    if (!targetCustomLineItem) {
      actions.push(defaultInsertAction)
      continue
    }

    const customFields = targetCustomLineItem.custom.fields
    const customPriceValue =
      typeof customFields?.customPrice?.centAmount === "number"
        ? customFields?.skuPrice * 100 || customFields?.customPrice?.centAmount
        : null

    // recalculate the price
    const lineItemCustomPrice = await getSkuCalculatedPrice({
      sku: item.sku,
      channels: storeKeysWithChannels,
      group: customerGroup,
      country: customerCart.billingAddress.country,
      discountPercent: customFields?.discountPercent,
      customPrice: customPriceValue,
      currencyCode,
      articleConfig,
      quantity: Number(itemQuantity) + Number(targetCustomLineItem.quantity),
    })

    let updatedCustomLineItem = getUpdatedLineItem({
      targetCustomLineItem,
      valuesToUpdate: item,
      customTypeId: customTypeLineItem.id,
      price: lineItemCustomPrice,
    })

    // line item already exists
    if (customFields?.productId === item.productKey && customFields?.sku === item.sku) {
      if (!customFields?.priceSku || customFields?.priceSku !== lineItemCustomPrice?.priceSku) {
        actions.push(removeCustomLineItemAction(targetCustomLineItem?.id))
        actions.push(
          addCustomLineItemAction(
            updatedCustomLineItem,
            currencyCode,
            lineItemCustomPrice,
            customerCart.id,
            Number(itemQuantity) + Number(targetCustomLineItem.quantity),
          ),
        )

        continue
      }

      if (item?.additionalInformation && item?.additionalInformation !== customFields?.additionalInformation) {
        updatedCustomLineItem.custom.fields.additionalInformation = item?.additionalInformation
        actions.push(removeCustomLineItemAction(targetCustomLineItem?.id))
        actions.push(
          addCustomLineItemAction(
            updatedCustomLineItem,
            currencyCode,
            lineItemCustomPrice,
            customerCart.id,
            Number(itemQuantity) + Number(targetCustomLineItem.quantity),
          ),
        )

        continue
      }

      if (customFields?.priceId === "custom") {
        // nothing to remove as the calculations should be the same as by the time the line item was updated
        actions.push(
          addCustomLineItemAction(
            updatedCustomLineItem,
            currencyCode,
            { key: "custom", skuPrice: price?.skuPrice, value: targetCustomLineItem.money },
            customerCart.id,
            Number(itemQuantity),
          ),
        )

        continue
      } else {
        // price is not custom and is different from what was found when line item was created
        if (customFields?.priceId !== price.key) {
          actions.push(removeCustomLineItemAction(targetCustomLineItem?.id))
          actions.push(
            addCustomLineItemAction(
              updatedCustomLineItem,
              currencyCode,
              lineItemCustomPrice,
              customerCart.id,
              Number(itemQuantity) + Number(targetCustomLineItem.quantity),
            ),
          )

          continue
        } else {
          // price is not custom, but still the same as the original
          // in theory logic should be the same as with the custom one, but separating it for better readability
          actions.push(
            addCustomLineItemAction(
              updatedCustomLineItem,
              currencyCode,
              lineItemCustomPrice,
              customerCart.id,
              Number(itemQuantity),
            ),
          )

          continue
        }
      }
    }

    for (let customLineItem of customLineItems) {
      if (!customLineItem) continue

      // old line item and we should remove it, custom fields are missing
      if (!customLineItem.custom || !customLineItem.custom.fields) {
        actions.push(removeCustomLineItemAction(customLineItem?.id))
        actions.push(defaultInsertAction)

        continue
      }
    }
  }

  return actions.filter((n) => n)
}

/**
 * Update customer cart
 *
 * @param {{ customerCart: object, actions: array }}
 * @returns {Promise<object>}
 */
export const updateCustomerCart = async ({ customerCart, actions }) => {
  const commerceApi = CommerceService.api()

  if (!customerCart || !actions) {
    throw new Error("Missing required parameters for updating customer cart")
  }

  if (actions.length < 1) {
    return customerCart
  }

  const payload = {
    version: customerCart.version,
    actions,
    storeKey: customerCart?.store?.key,
  }

  let updatedCart = (
    await commerceApi
      .carts()
      .withId({ ID: customerCart.id })
      .post({
        body: JSON.stringify(payload),
      })
      .execute()
  )?.body

  return updatedCart
}
